import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../../../services/api";
import { API_BASE_URL } from "../../../../config/apiConfig";
import { ProfileField, ProfileFieldGrid, ProfilePanel } from "./ProfileSection";
import { formatCurrencyInr, formatProfileDate } from "../employeeProfileUtils";
import {
  ArrowUpRight,
  BadgeCheck,
  CircleCheck,
  CircleX,
  Fuel,
  Gauge,
  IndianRupee,
  Info,
  ReceiptText,
  Settings2,
  Wallet,
} from "lucide-react";

export function mediaUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const origin = API_BASE_URL.replace(/\/api\/?$/i, "");
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function statusClass(status) {
  const key = String(status || "").toUpperCase();
  if (key === "APPROVED") return "ep-fuel-status ep-fuel-status--approved";
  if (key === "REJECTED") return "ep-fuel-status ep-fuel-status--rejected";
  return "ep-fuel-status ep-fuel-status--pending";
}

const MANAGE_PATH = "/settings/allowances-benefits/fuel-reimbursement";

/**
 * People Hub — read-only fuel reimbursement summary.
 * Mutations live under Settings → Allowances & Benefits → Fuel Reimbursement.
 */
export default function FuelReimbursementSection({ employeeId, hideWhenDisabled = false }) {
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [month, setMonth] = useState(currentMonthValue);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadBills = useCallback(async () => {
    if (!employeeId) return;
    const [year, monthNum] = month.split("-").map((part) => Number(part));
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/fuel-reimbursement/employees/${employeeId}/bills`, {
        params: { year, month: monthNum },
      });
      setPayload(res.data);
      if (typeof res.data?.enabled === "boolean") setEnabled(Boolean(res.data.enabled));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setPayload(null);
      } else {
        setError(err?.response?.data?.detail || "Unable to load fuel bills.");
      }
    } finally {
      setLoading(false);
    }
  }, [employeeId, month]);

  useEffect(() => {
    let cancelled = false;
    async function loadSetting() {
      try {
        const res = await API.get("/fuel-reimbursement/settings");
        if (cancelled) return;
        setEnabled(Boolean(res.data?.enabled));
      } catch {
        if (!cancelled) setEnabled(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    }
    loadSetting();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !employeeId) return;
    loadBills();
  }, [ready, employeeId, loadBills]);

  const bills = useMemo(() => payload?.bills || [], [payload]);

  const remaining = useMemo(() => {
    if (payload?.monthly_limit == null) return null;
    const limit = Number(payload.monthly_limit) || 0;
    const used = Number(payload.total_approved) || 0;
    return Math.max(0, limit - used);
  }, [payload]);

  useEffect(() => {
    if (!ready) return undefined;
    if (window.location.hash !== "#fuel-reimbursement") return undefined;
    const node = document.getElementById("fuel-reimbursement");
    if (!node) return undefined;
    const timer = window.setTimeout(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [ready, enabled]);

  if (!ready) return null;

  if (!enabled && hideWhenDisabled && !bills.length && !payload?.eligible) {
    return null;
  }

  const monthLabel = (() => {
    const d = new Date(`${month}-01T00:00:00`);
    return Number.isNaN(d.getTime()) ? month : d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  })();

  return (
    <div id="fuel-reimbursement" className="ep-fuel">
      <ProfilePanel
        title="Fuel Reimbursement"
        subtitle="Fuel bills and monthly limit · read-only"
        icon={Fuel}
        tone="indigo"
        actions={
          <>
            <label className="ep-month-picker">
              <span className="ep-sr-only">Month</span>
              <input type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
            </label>
            <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => setDrawerOpen(true)}>
              View Fuel Bills
            </button>
          </>
        }
      >
        {!enabled ? (
          <p className="ep-inline-note ep-inline-note--warn">
            <Info size={16} aria-hidden />
            <span>Fuel reimbursement is currently OFF for this organisation. Historical bills remain available.</span>
          </p>
        ) : null}

        <ProfileFieldGrid columns={4}>
          <ProfileField
            icon={payload?.eligible ? BadgeCheck : CircleX}
            tone={payload?.eligible ? "green" : "rose"}
            label="Eligibility"
            value={payload?.eligible ? "Eligible" : "Not eligible"}
          />
          <ProfileField
            icon={Gauge}
            tone="indigo"
            label="Monthly Limit"
            value={payload?.monthly_limit != null ? formatCurrencyInr(payload.monthly_limit) : "No limit"}
          />
          <ProfileField icon={IndianRupee} tone="violet" label="Used This Month" value={money(payload?.total_approved)} />
          <ProfileField icon={Wallet} tone="teal" label="Remaining" value={remaining == null ? "—" : money(remaining)} />
        </ProfileFieldGrid>

        <h4 className="ep-subhead">Bills for {monthLabel}</h4>
        <ProfileFieldGrid columns={3}>
          <ProfileField icon={ReceiptText} tone="blue" label="Submitted" value={money(payload?.total_submitted)} />
          <ProfileField icon={CircleCheck} tone="green" label="Approved" value={money(payload?.total_approved)} />
          <ProfileField icon={CircleX} tone="rose" label="Rejected" value={money(payload?.total_rejected)} />
        </ProfileFieldGrid>

        {error ? <p className="ep-fuel-error" role="alert">{error}</p> : null}
        {loading ? <p className="ep-fuel-note">Loading fuel bills…</p> : null}

        <p className="ep-manage-link">
          <Settings2 size={15} aria-hidden />
          <span>Managed in</span>
          <Link to={MANAGE_PATH}>
            Settings → Allowances &amp; Benefits → Fuel Reimbursement
            <ArrowUpRight size={14} aria-hidden />
          </Link>
        </p>
      </ProfilePanel>

      {drawerOpen ? (
        <div className="ep-advance-modal" role="presentation">
          <button
            type="button"
            className="ep-advance-backdrop"
            aria-label="Close"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="ep-advance-dialog" style={{ maxWidth: 560 }}>
            <h3>Fuel bills</h3>
            <p>
              {formatProfileDate(`${month}-01`)} · {bills.length} bill{bills.length === 1 ? "" : "s"}
            </p>
            {bills.length === 0 ? (
              <div className="ep-fuel-empty">
                <strong>No fuel bills for this month</strong>
                <span>Bills are managed under Allowances & Benefits.</span>
              </div>
            ) : (
              <div className="ep-table-wrap">
                <table className="ep-table">
                  <thead>
                    <tr>
                      <th>Bill Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill) => (
                      <tr key={bill.id}>
                        <td>{formatProfileDate(bill.bill_date)}</td>
                        <td>{money(bill.amount)}</td>
                        <td>
                          <span className={statusClass(bill.status)}>
                            {String(bill.status || "").toLowerCase()}
                          </span>
                        </td>
                        <td>
                          {bill.attachment ? (
                            <a href={mediaUrl(bill.attachment)} target="_blank" rel="noreferrer">
                              Attachment
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="ep-advance-dialog-actions">
              <Link className="ep-btn ep-btn-secondary" to={MANAGE_PATH}>
                Manage in Settings
              </Link>
              <button type="button" className="ep-btn ep-btn-ghost" onClick={() => setDrawerOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
